// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
/**
 * Upgrade Modal Component
 * Shows pricing and feature comparison when user hits limits
 */

import React, { useState } from 'react';
import axios from 'axios';

const UpgradeModal = ({ isOpen, onClose, triggerReason, userData }) => {
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const hasUsedTrial = userData?.trial_used === true || userData?.trial_end_date != null;
  
  if (!isOpen) return null;
  
  // Different messages based on what triggered the upgrade prompt
  const getTriggerMessage = () => {
    switch(triggerReason) {
      case 'comparison_limit':
        return "You've reached the free plan limit of 5 product comparisons";
      case 'store_limit':
        return "You've reached the free plan limit of 3 stores per comparison";
      case 'saved_list_limit':
        return "You've reached the free plan limit of 3 saved shopping lists";
      case 'premium_feature':
        return "This feature is available on Pro and Family plans";
      default:
        return "Upgrade to unlock all premium features";
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl sticky top-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold mb-2">Upgrade to Premium</h2>
              <p className="text-blue-100">{getTriggerMessage()}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-8">
          {/* Feature Comparison Table */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Choose Your Plan
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-bold text-gray-900">Feature</th>
                    <th className="text-center py-4 px-4">
                      <div className="font-bold text-gray-900">Free</div>
                      <div className="text-sm text-gray-600">£0/mo</div>
                    </th>
                    <th className="text-center py-4 px-4 bg-blue-50">
                      <div className="font-bold text-blue-900">Pro</div>
                      <div className="text-sm text-blue-600">£1.99/mo</div>
                    </th>
                    <th className="text-center py-4 px-4 bg-purple-50">
                      <div className="font-bold text-purple-900">Family</div>
                      <div className="text-sm text-purple-600">£2.99/mo</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Product comparisons</td>
                    <td className="text-center py-3 px-4">5 items</td>
                    <td className="text-center py-3 px-4 bg-blue-50">Unlimited</td>
                    <td className="text-center py-3 px-4 bg-purple-50">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Stores per comparison</td>
                    <td className="text-center py-3 px-4">3 stores</td>
                    <td className="text-center py-3 px-4 bg-blue-50">All stores</td>
                    <td className="text-center py-3 px-4 bg-purple-50">All stores</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Saved shopping lists</td>
                    <td className="text-center py-3 px-4">3 lists</td>
                    <td className="text-center py-3 px-4 bg-blue-50">Unlimited</td>
                    <td className="text-center py-3 px-4 bg-purple-50">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Cart Optimizer</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-blue-50">✅</td>
                    <td className="text-center py-3 px-4 bg-purple-50">✅</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Bulk Comparison</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-blue-50">✅</td>
                    <td className="text-center py-3 px-4 bg-purple-50">✅</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Kitchen AI</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-blue-50">✅</td>
                    <td className="text-center py-3 px-4 bg-purple-50">✅</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Fuel Price Finder</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-blue-50">✅</td>
                    <td className="text-center py-3 px-4 bg-purple-50">✅</td>
                  </tr>
                  {/* Hair & Beauty row removed 2026-06-29 — feature archived */}
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Loyalty Wallet</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-blue-50">✅</td>
                    <td className="text-center py-3 px-4 bg-purple-50">✅</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Family members</td>
                    <td className="text-center py-3 px-4">1</td>
                    <td className="text-center py-3 px-4 bg-blue-50">1</td>
                    <td className="text-center py-3 px-4 bg-purple-50 font-bold">4</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Shared shopping lists</td>
                    <td className="text-center py-3 px-4">❌</td>
                    <td className="text-center py-3 px-4 bg-blue-50">❌</td>
                    <td className="text-center py-3 px-4 bg-purple-50 font-bold">✅</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Ads</td>
                    <td className="text-center py-3 px-4">Shown</td>
                    <td className="text-center py-3 px-4 bg-blue-50">No ads</td>
                    <td className="text-center py-3 px-4 bg-purple-50">No ads</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Plan Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Pro Plan */}
            <div 
              onClick={() => setSelectedPlan('pro')}
              className={`border-2 rounded-2xl p-6 cursor-pointer transition ${
                selectedPlan === 'pro' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-2xl font-bold text-gray-900">Pro</h4>
                  <p className="text-sm text-gray-600">Perfect for individuals</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === 'pro' ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                }`}>
                  {selectedPlan === 'pro' && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-4xl font-bold text-blue-600">£1.99</div>
                <div className="text-gray-600">per month</div>
              </div>
              
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  All premium features
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  7-day free trial
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  No ads
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Cancel anytime
                </li>
              </ul>
            </div>
            
            {/* Family Plan */}
            <div 
              onClick={() => setSelectedPlan('family')}
              className={`border-2 rounded-2xl p-6 cursor-pointer transition relative ${
                selectedPlan === 'family' 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="absolute -top-3 -right-3 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                BEST VALUE
              </div>
              
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-2xl font-bold text-gray-900">Family</h4>
                  <p className="text-sm text-gray-600">Save together with family</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === 'family' ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
                }`}>
                  {selectedPlan === 'family' && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-4xl font-bold text-purple-600">£2.99</div>
                <div className="text-gray-600">per month</div>
                <div className="text-sm text-purple-600 font-medium mt-1">£1.75 per person</div>
              </div>
              
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  All Pro features
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Up to 4 family members
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Shared shopping lists
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Family savings dashboard
                </li>
              </ul>
            </div>
          </div>
          
          {/* Student Discount Banner */}
          <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-4 mb-6 text-center">
            <p className="text-sm text-gray-700">
              🎓 <span className="font-bold">Students get 50% off!</span> Verify with UNiDAYS to unlock your discount
            </p>
          </div>
          
          {/* Action Button */}
          <button
            disabled={loading}
            onClick={async () => {
              const tierId = selectedPlan === 'family' ? 'family' : 'individual';
              setLoading(true);
              try {
                const res = await axios.post(`${backendUrl}/api/stripe-payments/checkout/create`, {
                  tier_id: tierId,
                  billing_cycle: 'monthly',
                  origin_url: window.location.origin
                }, { withCredentials: true });
                if (res.data?.url || res.data?.checkout_url) {
                  window.location.href = res.data.url || res.data.checkout_url;
                } else {
                  alert('Could not start checkout. Please try again.');
                }
              } catch (err) {
                console.error('Checkout error:', err);
                alert(`Upgrade failed: ${err.response?.data?.detail || err.message}`);
              } finally {
                setLoading(false);
              }
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl text-lg transition shadow-lg"
          >
            {loading ? 'Redirecting to checkout...' : `Upgrade to ${selectedPlan === 'pro' ? 'Premium' : 'Family'} — £${selectedPlan === 'pro' ? '1.99' : '2.99'}/mo`}
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            Secure checkout powered by Stripe • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
